import { ChevronRightIcon, LockIcon, RouteIcon } from "lucide-react";
import Link from "next/link";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import type { WorkspaceChannel } from "@/lib/mlx-workspace-client";

/*
 * The channel toolbar as markup. ChannelWorkspaceToolbar owns the channel's
 * data, its access query and sheet, and renders its title row and actions
 * through these, so a fixture channel composes the same header.
 */

/** The channel page's title row: "Channel › Data set" inside a data set, with the channel's actions at the end. */
export function ChannelWorkspaceToolbarView({
  channel,
  dataSet,
  end,
}: {
  /** Null while the channel loads, when the title reads "Channel". */
  channel: Pick<WorkspaceChannel, "id" | "name" | "purpose"> | null;
  /** The open data set, named after the channel. */
  dataSet?: { name: string } | null;
  end?: ReactNode;
}) {
  return (
    <header className="mlx-page-toolbar">
      <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2">
          <h1
            className="mlx-page-toolbar-title channel-workspace-toolbar-title shrink-0"
            title={channel?.purpose || channel?.name}
          >
            {channel ? (
              <Link
                href={`/workspace/channels/${encodeURIComponent(channel.id)}`}
                className="block min-w-0 truncate rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {channel.name}
              </Link>
            ) : (
              "Channel"
            )}
          </h1>
          {dataSet ? (
            <>
              <ChevronRightIcon
                aria-hidden
                className="size-3.5 shrink-0 text-muted-foreground"
              />
              <span
                aria-current="page"
                className="min-w-0 truncate text-sm font-medium"
                title={dataSet.name}
              >
                {dataSet.name}
              </span>
            </>
          ) : null}
        </div>
        <div className="mlx-page-toolbar-actions channel-workspace-toolbar-actions">
          {end}
        </div>
      </div>
    </header>
  );
}

/** The channel's end actions: the open data set's freshness, a divider, then the channel's own actions. */
export function ChannelWorkspaceToolbarEndView({
  freshness,
  actions,
}: {
  /** Shown only for data with a source; data stored in Twin is always current. */
  freshness?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <>
      {freshness}
      <div
        aria-hidden
        className="channel-workspace-toolbar-divider h-5 w-px shrink-0 bg-border/70"
      />
      {actions}
    </>
  );
}

/** On a dashboard: opens the trace of the data behind its widgets. */
export function ChannelTraceDashboardButton({
  onClick,
}: {
  onClick?: () => void;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="shrink-0 px-1.5"
      aria-label="Trace dashboard data"
      title="Trace dashboard data"
      onClick={onClick}
    >
      <RouteIcon data-icon="inline-start" />
      <span className="channel-workspace-toolbar-action-label">
        Trace data
      </span>
    </Button>
  );
}

/** Opens the channel's access settings, for people who can manage them. */
export function ChannelAccessButton({ onClick }: { onClick?: () => void }) {
  return (
    <Button
      size="icon-sm"
      variant="outline"
      aria-label="Channel access"
      title="Channel access"
      onClick={onClick}
    >
      <LockIcon className="size-3.5" />
    </Button>
  );
}
